import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from './components/layout';
import Loader from './components/loader';
import { ConfigProvider, message } from 'antd';
import { commonActions } from './actions/commonActions';
import authAction from './actions/authActions';
import PublicRoute from './routes/publicRoute';
import SubscriptionSuccess from './pages/layout/payment';
import { trackPageView, applyPageSEO } from './utils/analytics';
import { getblobLink } from '../src/services/helper';
// import SchoolDirectoryPage from './pages/landing/schoolDirectory';     './pages/landing/schoolDirectory';
const SchoolDirectory = lazy(() => import('./pages/landing/schoolDirectory'));

const Home = lazy(() => import('./pages/dashboard/home'));
const Login = lazy(() => import('./pages/authentication/login'));
const OtpVerification = lazy(() => import('./pages/authentication/otpVerification'));
const DeviceRestriction = lazy(() => import('./pages/authentication/deviceRestriction'));
const Profile = lazy(() => import('./pages/authentication/profile'));
const AcceptInvitation = lazy(() => import('./pages/authentication/acceptInvitation'));
const Services = lazy(() => import('./pages/services/services'));
const UserList = lazy(() => import('./pages/users/userList'));
const UserView = lazy(() => import('./pages/users/userView'));
const Batches = lazy(() => import('./pages/batches/batches'));
const BatchView = lazy(() => import('./pages/batches/batchView'));
const BatchTripSheets = lazy(() => import('./pages/batches/batchTripSheets'));
const BatchTracking = lazy(() => import('./pages/batches/batchTracking'));
const BatchHistory = lazy(() => import('./pages/batches/batchHistory'));
const Vehicles = lazy(() => import('./pages/vehicles/vehicles'));
const Expenses = lazy(() => import('./pages/expenses/expenses'));
const FeeView = lazy(() => import('./pages/expenses/feeView'));
const Enrollments = lazy(() => import('./pages/enrollments/enrollment'));
const EnrollmentView = lazy(() => import('./pages/enrollments/enrollmentView'));
const SessionDetails = lazy(() => import('./pages/enrollments/sessionDetails'));
const PaymentHistory = lazy(() => import('./pages/enrollments/paymentHistory'));
const Category = lazy(() => import('./pages/categories/category'));
const QueryList = lazy(() => import('./pages/queries/queries'));
const LeadList = lazy(() => import('./pages/leads/leads'));
const LeadView = lazy(() => import('./pages/leads/leadView'));
const Settings = lazy(() => import('./pages/settings/settings'));
const Landing = lazy(() => import('./pages/landing/landing'));
const Pricing = lazy(() => import('./pages/landing/pricing'));
const PrivacyPolicy = lazy(() => import('./pages/layout/privacyPolicy'));
const TermsOfService = lazy(() => import('./pages/layout/termsOfService'));
const ReleaseNotes = lazy(() => import('./pages/layout/releaseNotes'));
const Register = lazy(() => import('./pages/authentication/register'));
const VerifyRegistration = lazy(() => import('./pages/authentication/verifyRegistration'));
const TenantList = lazy(() => import('./pages/tenants/tenantList'));
const TenantView = lazy(() => import('./pages/tenants/tenantView'));
const PurchaseHistory = lazy(() => import('./pages/tenants/purchaseHistory'));
const SubscriptionPlans = lazy(() => import('./pages/subscriptions/subscriptionPlans'));
const SubscriptionPlanForm = lazy(() => import('./pages/subscriptions/subscriptionPlanForm'));
const NotFound = lazy(() => import('./components/notFound'));
const PublicTracking = lazy(() => import('./pages/tracking/publicTracking'));
const RoleAccess = lazy(() => import('./components/documents/RoleAccess'));
const UserGuide = lazy(() => import('./components/documents/UserGuide'));

const RouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    applyPageSEO(location.pathname);
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  return null;
};

const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {

  const authData = typeof window !== 'undefined' ? localStorage.getItem('AUTHDATA') : null;
  if (!authData) {
    return <Navigate to="/" replace />;
  }
  return <MainLayout>{children}</MainLayout>;
};

const App: React.FC = () => {

  const status = useSelector((state: any) => state.commonReducer);
  const verificationStatus = useSelector((state: any) => state.verifyTokenReducer);
  const dispatch = useDispatch();
  const data = localStorage.getItem('AUTHDATA');
  const roleAccess = JSON.parse(String(data));

  useEffect(() => {
    message.config({ top: 100 });
  }, []);


  useEffect(() => {
    const success = Boolean(status?.isSuccess);
    const error = Boolean(status?.isError);
    const msg = success ? status?.message : error ? status?.error : undefined;
    if (!msg) return;
    const text = String(msg);
    if (success) {
      message.success(text);
    } else if (error) {
      message.error(text);
    }
    dispatch(commonActions.clearMessage());
  }, [status?.isSuccess, status?.isError, status?.message, status?.error, dispatch]);

  useEffect(() => {
    const authData = localStorage.getItem('AUTHDATA');
    if (authData) {
      const parsedAuthData = JSON.parse(authData);
      dispatch(authAction.verifyTokenRequest({ token: parsedAuthData?.authToken, isInvite: false }));
    }
  }, []);

  useEffect(() => {
    if (verificationStatus?.isResponse) {
      if (!verificationStatus?.isSuccess) {
        localStorage.removeItem('AUTHDATA');        
        window.location.replace('/login');
      }
    }
  }, [verificationStatus.isResponse, verificationStatus.isSuccess]);


  return (
    <ConfigProvider
    theme={{
      token: {
        colorPrimary: '#4f46e5',
        borderRadius: 8,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    }}
    >
      <Router>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            width: '100vw'
          }}>
            <img src={`${getblobLink()}/public/asset/logo_with_name.png`} alt="DriveMynt Logo"
              loading="lazy" width="180" height="180"
            />
          </div>
        }>
          <RouteTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<PublicRoute allowAuthenticated={true}><Pricing /></PublicRoute>} />
            <Route path="/roleAccess" element={<PublicRoute allowAuthenticated={true}><RoleAccess/></PublicRoute>} />
            <Route path="/userGuide" element={<PublicRoute allowAuthenticated={true}><UserGuide/></PublicRoute>} />
            <Route path="/privacy-policy" element={<PublicRoute allowAuthenticated={true}><PrivacyPolicy /></PublicRoute>} />
            <Route path="/terms-of-service" element={<PublicRoute allowAuthenticated={true}><TermsOfService /></PublicRoute>} />
            <Route path="/driveMynt-versions" element={<PublicRoute allowAuthenticated={true}><ReleaseNotes /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/verify-registration" element={<VerifyRegistration />} />
            <Route path="/verify-otp" element={<PublicRoute allowAuthenticated={true}><OtpVerification /></PublicRoute>} />
            <Route path="/device-restriction" element={<PublicRoute allowAuthenticated={true} ><DeviceRestriction /></PublicRoute>} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/dashboard" element={<ProtectedRoute><Home role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/enrollments" element={<ProtectedRoute><Enrollments role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/enrollments/view" element={<ProtectedRoute><EnrollmentView role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/enrollments/sessions" element={<ProtectedRoute><SessionDetails /></ProtectedRoute>} />
            <Route path="/enrollments/payments" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
            <Route path="/users/view" element={<ProtectedRoute><UserView /></ProtectedRoute>} />
            <Route path="/services" element={<ProtectedRoute><Services role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/batches" element={<ProtectedRoute><Batches role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/batches/view" element={<ProtectedRoute><BatchView role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/batches/tripsheets" element={<ProtectedRoute><BatchTripSheets /></ProtectedRoute>} />
            <Route path="/batches/tracking" element={<ProtectedRoute><BatchTracking /></ProtectedRoute>} />
            <Route path="/batches/history" element={<ProtectedRoute><BatchHistory /></ProtectedRoute>} />
            <Route path="/vehicles" element={<ProtectedRoute><Vehicles role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/queries" element={<ProtectedRoute><QueryList role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/leads" element={<ProtectedRoute><LeadList /></ProtectedRoute>} />
            <Route path="/leads/view" element={<ProtectedRoute><LeadView /></ProtectedRoute>} />
            <Route path="/categories" element={<ProtectedRoute><Category role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/tenants" element={<ProtectedRoute><TenantList /></ProtectedRoute>} />
            <Route path="/tenants/view" element={<ProtectedRoute><TenantView role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/tenants/purchaseHistory" element={<ProtectedRoute><PurchaseHistory /></ProtectedRoute>} />
            <Route path="/subscription-plans" element={<ProtectedRoute><SubscriptionPlans role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/subscription-plans/form" element={<ProtectedRoute><SubscriptionPlanForm role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/accounting" element={<ProtectedRoute><Expenses role={roleAccess?.role} /></ProtectedRoute>} />
  
            <Route path="/fees/view" element={<ProtectedRoute><FeeView role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile role={roleAccess?.role} /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to={Boolean(data) ? "/dashboard" : "/login"} replace />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/track/:token" element={<PublicTracking />} />
            <Route path="/schools" element={<SchoolDirectory />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </ConfigProvider>
  );
};

export default App;
